import { createTheme, type Theme } from "@mui/material/styles";

export type BrandName = "gastrosoft" | "orda-pos";

/** Per-brand accent palette (identical across light/dark; only neutrals flip). */
interface BrandPalette {
  primary: { main: string; light: string; dark: string; contrastText: string };
  secondary: {
    main: string;
    light: string;
    dark: string;
    contrastText: string;
  };
  info: { main: string; light: string; dark: string };
}

const brandPalettes: Record<BrandName, BrandPalette> = {
  gastrosoft: {
    primary: {
      main: "#004D97",
      light: "#336BA8",
      dark: "#003670",
      contrastText: "#fff",
    },
    secondary: {
      main: "#ff4d00",
      light: "#ffb74d",
      dark: "#db4402",
      contrastText: "#fff",
    },
    info: { main: "#3B82F6", light: "#60A5FA", dark: "#2563EB" },
  },
  "orda-pos": {
    primary: {
      main: "#ADCC53",
      light: "#B8D87A",
      dark: "#7B9E3E",
      contrastText: "#fff",
    },
    secondary: {
      main: "#ffbc59",
      light: "#fabf69",
      dark: "#e0a651",
      contrastText: "#fff",
    },
    info: { main: "#9E9E9E", light: "#E0E0E0", dark: "#616161" },
  },
};

const statusColors = {
  error: { main: "#EF4444", light: "#F87171", dark: "#DC2626" },
  warning: { main: "#F59E0B", light: "#FBBF24", dark: "#D97706" },
  success: { main: "#10B981", light: "#34D399", dark: "#059669" },
};

// Neutrals: light values are backoffice-canonical (kept exact); dark mirrors the reseller palette.
const lightNeutrals = {
  background: { default: "#F8FAFC", paper: "#ffffff" },
  text: { primary: "#1E293B", secondary: "#64748B" },
  divider: "#E2E8F0",
};

const darkNeutrals = {
  background: { default: "#0f172a", paper: "#1e293b" },
  text: { primary: "#f8fafc", secondary: "#94a3b8" },
  divider: "#334155",
};

const typography = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  h1: { fontSize: "2.5rem", fontWeight: 700, letterSpacing: "-0.02em" },
  h2: { fontSize: "2rem", fontWeight: 700, letterSpacing: "-0.02em" },
  h3: { fontSize: "1.75rem", fontWeight: 600, letterSpacing: "-0.01em" },
  h4: { fontSize: "1.5rem", fontWeight: 600, letterSpacing: "-0.01em" },
  h5: { fontSize: "1.25rem", fontWeight: 600 },
  h6: { fontSize: "1rem", fontWeight: 600 },
  body1: { fontSize: "1rem", lineHeight: 1.6 },
  body2: { fontSize: "0.875rem", lineHeight: 1.5 },
} as const;

const deepShadow = "0 25px 50px -12px rgb(0 0 0 / 0.25)";
const shadows = [
  "none",
  "0 1px 2px 0 rgb(0 0 0 / 0.05)",
  "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
  "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
  "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
  ...Array<string>(19).fill(deepShadow),
] as unknown as Theme["shadows"];

export function buildBrandTheme(brand: BrandName): Theme {
  const accent = brandPalettes[brand];

  return createTheme({
    cssVariables: { colorSchemeSelector: "class" },
    colorSchemes: {
      light: { palette: { ...accent, ...statusColors, ...lightNeutrals } },
      dark: { palette: { ...accent, ...statusColors, ...darkNeutrals } },
    },
    typography,
    shape: { borderRadius: 12 },
    shadows,
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: 8,
            fontWeight: 600,
            padding: "8px 16px",
            boxShadow: "none",
            "&:hover": {
              boxShadow:
                "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
            },
          },
          contained: {
            "&:hover": {
              boxShadow:
                "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 12,
            boxShadow:
              "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
            border: "1px solid #E2E8F0",
            ...theme.applyStyles("dark", { border: "1px solid #334155" }),
          }),
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none" },
          elevation1: { boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)" },
          elevation2: {
            boxShadow:
              "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow:
              "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
            backgroundImage: "none",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRight: "1px solid #E2E8F0",
            boxShadow: "none",
            ...theme.applyStyles("dark", { borderRight: "1px solid #334155" }),
          }),
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: { "& .MuiOutlinedInput-root": { borderRadius: 8 } },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 6, fontWeight: 500 },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: ({ theme }) => ({
            "& .MuiTableCell-root": {
              borderColor: "#E2E8F0",
              ...theme.applyStyles("dark", { borderColor: "#334155" }),
            },
          }),
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: ({ theme }) => ({
            "& .MuiTableCell-root": {
              backgroundColor: "#F8FAFC",
              color: "#1E293B",
              fontWeight: 600,
              fontSize: "0.875rem",
              borderBottom: "2px solid #E2E8F0",
              ...theme.applyStyles("dark", {
                backgroundColor: "#111c30",
                color: "#f8fafc",
                borderBottom: "2px solid #334155",
              }),
            },
          }),
        },
      },
      MuiTableBody: {
        styleOverrides: {
          root: ({ theme }) => ({
            "& .MuiTableRow-root": {
              "&:nth-of-type(even)": {
                backgroundColor: "#FAFBFC",
                ...theme.applyStyles("dark", { backgroundColor: "#172033" }),
              },
              "&:hover": {
                backgroundColor: "#F1F5F9",
                ...theme.applyStyles("dark", { backgroundColor: "#1e293b" }),
              },
              "&.Mui-selected": {
                backgroundColor: "#E0E7FF",
                "&:hover": { backgroundColor: "#C7D2FE" },
                ...theme.applyStyles("dark", {
                  backgroundColor: "#1e3a5f",
                  "&:hover": { backgroundColor: "#25497a" },
                }),
              },
            },
            "& .MuiTableCell-root": {
              color: "#1E293B",
              borderBottom: "1px solid #E2E8F0",
              fontSize: "0.875rem",
              ...theme.applyStyles("dark", {
                color: "#f8fafc",
                borderBottom: "1px solid #334155",
              }),
            },
          }),
        },
      },
    },
  });
}
